//
// Verbraucher (appliances): Icon-Vokabular und Messpunkt-Helfer.
//
// Der Icon-Schluessel ist der Asset-Name der iOS-App (Constants.
// sensorTypeImages), damit Web und Apps dieselbe Kennung speichern. Im Web
// wird er auf ein Font-Awesome-Icon abgebildet; `types` sind die
// Sensortypen (0 Strom, 1 Gas, 2 Wasser, 3 Waerme), fuer die das Icon
// angeboten wird.
//

export const APPLIANCE_ICONS = {
  Fridge: { icon: 'snowflake', types: [0] },
  Freezer: { icon: 'snowflake', types: [0] },
  TV: { icon: 'tv', types: [0] },
  'Washing Machine': { icon: 'jug-detergent', types: [0, 2] },
  'Tumble Dryer': { icon: 'fan', types: [0, 1] },
  Dishwasher: { icon: 'kitchen-set', types: [0, 2] },
  Stove: { icon: 'fire-burner', types: [0, 1] },
  'Coffee Maker': { icon: 'mug-hot', types: [0] },
  Lamp: { icon: 'lightbulb', types: [0] },
  'PC ON Desk': { icon: 'computer', types: [0] },
  Car: { icon: 'charging-station', types: [0] },
  Plug: { icon: 'plug', types: [0] },
  'Heat Pump': { icon: 'wind', types: [0, 3] },
  Pump: { icon: 'gear', types: [0, 2] },
  'Water Heater': { icon: 'temperature-high', types: [0, 1, 2, 3] },
  'Heating Radiator': { icon: 'fire', types: [1, 3] },
  Boiler: { icon: 'fire-flame-simple', types: [1, 3] },
  'Water Tap': { icon: 'faucet', types: [2] },
  Shower: { icon: 'shower', types: [2, 3] },
  Bath: { icon: 'bath', types: [2, 3] },
  Toilet: { icon: 'toilet', types: [2] },
  Sink: { icon: 'sink', types: [2] },
  Garden: { icon: 'seedling', types: [2] },
  Pool: { icon: 'water-ladder', types: [0, 2] },
  Other: { icon: 'tag', types: [0, 1, 2, 3] }
}

const FALLBACK_BY_TYPE = { 0: 'plug', 1: 'fire', 2: 'droplet', 3: 'temperature-high' }

export function fallbackIconForType (type) {
  return FALLBACK_BY_TYPE[Number(type)] || 'tag'
}

// FA icon name for an appliance ({ icon, category }) — unknown or empty
// icon keys fall back to the category icon
export function faIconForAppliance (appliance) {
  if (!appliance) return 'tag'
  const entry = appliance.icon ? APPLIANCE_ICONS[appliance.icon] : null
  return entry ? entry.icon : fallbackIconForType(appliance.category)
}

// [{ key, icon }] offered for a sensor type, alphabetical by key
export function iconsForType (type) {
  const t = Number(type)
  return Object.keys(APPLIANCE_ICONS)
    .filter(key => APPLIANCE_ICONS[key].types.includes(t))
    .sort((a, b) => a.localeCompare(b))
    .map(key => ({ key, icon: APPLIANCE_ICONS[key].icon }))
}

function pointsOfSensor (appliance, sensorId) {
  return ((appliance && appliance.measurementpoints) || []).filter(mp => Number(mp.sensorid) === Number(sensorId))
}

// Union of the systemparts shown as tiles ([{systempart, valuetype}]) and
// the systemparts of already attached appliances, sorted ascending
export function systemPartsForSensor (tileValues, appliances, sensorId) {
  const parts = new Set()
  for (const v of tileValues || []) {
    if (v && v.systempart != null && v.systempart >= 0) parts.add(Number(v.systempart))
  }
  for (const a of appliances || []) {
    for (const mp of pointsOfSensor(a, sensorId)) {
      if (mp.systempart != null) parts.add(Number(mp.systempart))
    }
  }
  return Array.from(parts).sort((a, b) => a - b)
}

export function appliancesForPoint (appliances, sensorId, systempart) {
  return (appliances || []).filter(a => pointsOfSensor(a, sensorId).some(mp => Number(mp.systempart) === Number(systempart)))
}

// { systempart: [appliance] } — an appliance with two points on the same
// sensor appears in both rows
export function groupAppliancesBySystempart (appliances, sensorId) {
  const groups = {}
  for (const a of appliances || []) {
    for (const mp of pointsOfSensor(a, sensorId)) {
      const sp = Number(mp.systempart)
      if (!groups[sp]) groups[sp] = []
      if (!groups[sp].includes(a)) groups[sp].push(a)
    }
  }
  return groups
}

export function checkedSystempartsForSensor (appliance, sensorId) {
  return pointsOfSensor(appliance, sensorId).map(mp => Number(mp.systempart)).sort((a, b) => a - b)
}

// PATCH replaces the whole point list: keep the points of OTHER sensors,
// replace those of this sensor with the checked systemparts, dedupe
export function mergePointsForSensor (existingPoints, sensorId, checkedSystemparts) {
  const out = []
  const seen = new Set()
  const push = (sid, sp) => {
    const key = sid + ':' + sp
    if (seen.has(key)) return
    seen.add(key)
    out.push({ sensorid: sid, systempart: sp })
  }
  for (const mp of existingPoints || []) {
    if (!mp || mp.sensorid == null || mp.systempart == null) continue
    if (Number(mp.sensorid) === Number(sensorId)) continue
    push(Number(mp.sensorid), Number(mp.systempart))
  }
  for (const sp of checkedSystemparts || []) {
    push(Number(sensorId), Number(sp))
  }
  return out
}
